import DOMPurify from 'dompurify';
import {REPORT_ACTION, REPORT_ANGLE_PATTERN} from '../../constants/agentChat.constants';

/**
 * Sanitising untrusted HTML before it reaches the DOM.
 *
 * ⚠ WHY THIS EXISTS, AND WHAT WAS ACTUALLY EXPLOITABLE. Agent output reaches the screen as
 * `parse(marked(text))` — `marked` into `html-react-parser` — and `marked` sanitises nothing; its
 * `sanitize` option was removed years ago in favour of "use DOMPurify". Measured through the real
 * chain rather than assumed:
 *
 *   [click](javascript:alert(1))     ->  <a href="javascript:alert(1)">click</a>   REACHES THE DOM
 *   <a href="javascript:alert(1)">   ->  same                                      REACHES THE DOM
 *   <iframe src="data:text/html,…">  ->  element survives                          REACHES THE DOM
 *   <img src=x onerror=alert(1)>     ->  <img src="x">                             React drops it
 *   <svg onload=alert(1)>            ->  <svg></svg>                               React drops it
 *
 * React already refuses string event handlers, so the classic `onerror` payloads die on their own.
 * **The live vector is the URL scheme** — and the chat's own `replace` hook was adding
 * `target="_blank"` to anchors without ever looking at the href, so it helped the payload along.
 *
 * ⚠ THIS IS THE SECOND LAYER, NOT THE FIRST. The guarantee lives server-side, where the renderer
 * escapes values into the template (autoescape on, asserted by a pytest snapshot). A browser
 * sanitiser is what stands between a mistake there and a user's session — belt and braces, in that
 * order. Do not let this become the reason server-side escaping is skipped.
 */

/**
 * Allowed URL schemes — DOMPurify's own shape, narrowed to the three this product uses.
 *
 * ⚠ THE `|$` IS LOAD-BEARING AND ITS ABSENCE IS SILENT. `ALLOWED_URI_REGEXP` is tested against
 * EVERY attribute value, not only hrefs. A first version dropped that alternative, so a plain value
 * of lowercase letters with no scheme — `scope="col"`, `role="row"`, `dir="ltr"`, `align="right"` —
 * failed the test and DOMPurify stripped the attribute. Tables lost their header semantics and
 * nothing said so; the verifier caught it, reading by eye had not.
 *
 * Reject: `javascript:`, `vbscript:`, `data:`. Accept: the three schemes, anything starting with a
 * non-letter (`#anchor`, `/path`), and any scheme-less value.
 */
const SAFE_URL = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

const PROSE_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'code', 'pre',
  'a',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
];

/**
 * The report shell and its seven primitives (WIT-213): `header`, `lead`, `card`, `badge`,
 * `ranked_bars`, `table`, `actions`.
 *
 * ⚠ THE `style` ATTRIBUTE IS ALLOWED; THE `<style>` ELEMENT IS NOT, AND CANNOT BE. `ranked_bars`
 * is *divs with percentage widths* — no chart library, no SVG — so `style` on an element is not
 * optional, and `sanitizeStyleValue` below is what makes granting it bounded.
 *
 * `<style>` is a different matter: **DOMPurify empties its contents unconditionally**, even when
 * the tag is in `ALLOWED_TAGS`. Measured — `<style>:root{--label:#6b7280}</style>` comes back as
 * `<style></style>`. Listing it would therefore have been a lie that rendered the report unstyled
 * while the policy claimed otherwise.
 *
 * That is a real constraint on WIT-213, not a preference: **the report's `:root` token block cannot
 * survive this sanitiser**, so the tokens have to come from `app-desktop`'s own stylesheet rather
 * than ride in with the HTML. WIT-213 already says the values ARE app-desktop's, so shipping them
 * back to us was redundant anyway — but the plan should say so explicitly before the shell is
 * built, or the first render will be unstyled and nobody will know why.
 *
 * `details` and `summary` are the origin disclosure (WIT-251): a report carries no script, so a native disclosure is
 * the only one it can fold. Their one attribute, `open`, is below; an `ontoggle` goes with every other event handler.
 */
const REPORT_TAGS = [...PROSE_TAGS, 'section', 'article', 'header', 'footer', 'figure', 'figcaption', 'button',
  'details', 'summary'];

const PROSE_ATTR = ['href', 'title', 'alt', 'lang', 'dir', 'class', 'id',
  'colspan', 'rowspan', 'scope', 'headers', 'align',
  'role', 'aria-label', 'aria-labelledby', 'aria-describedby', 'aria-hidden'];

/**
 * ⚠ `button`, `type`, `disabled` AND `data-action` ARE HERE FOR THE REPORT'S ACTIONS, AND NOTHING MORE.
 * The report's quick actions — download the PDF, download the CSV — are buttons whose behaviour
 * app-desktop wires by `data-action`; while `<button>` was forbidden their text ran together as plain
 * text. A button is safe here only because of what it cannot carry: no `<form>` to submit (forbidden),
 * no `formaction`, and none of `popovertarget`, `commandfor`/`command` or `autofocus`, which let a
 * button open, close or focus things without any script. The hook below forces `type="button"` and
 * keeps `data-action` only for an action app-desktop handles.
 */
const REPORT_ATTR = [...PROSE_ATTR, 'style', 'width', 'height', 'data-figure', 'data-unit',
  'type', 'disabled', 'data-action', 'data-angle', 'aria-pressed', 'open'];

/** The only `data-action` values app-desktop recognises (`REPORT_ACTION`); any other is removed. */
const REPORT_ACTIONS = new Set(Object.values(REPORT_ACTION));

/**
 * The SVG a line chart and a bar chart need, and nothing else: WIT-255, for Slice B's `line_chart` and `legend`, and
 * `rect` (with `rx`/`ry`) for Slice C's vertical `bar_chart` (WIT-234).
 *
 * ⚠ SVG IS A SECOND WAY TO DRAW, SO THE SAME RULES BIND IT. The CSS list keeps a report inside its box
 * and off the network, and SVG has doors of its own to both. So the elements are a chart's drawing
 * primitives only, and what they leave out is what makes SVG dangerous here:
 *
 *  - `use`, `image` and `foreignObject` — an external document, an outbound request, and arbitrary
 *    HTML carried back in past this policy;
 *  - `animate` and `set` — they rewrite an attribute after sanitising, `href` included;
 *  - `style` — emptied anyway, as for HTML;
 *  - the `transform`, `filter`, `mask` and `clip-path` attributes, for the reasons their CSS forms are refused.
 *
 * ⚠ A PRESENTATION ATTRIBUTE IS CSS BY ANOTHER NAME. `fill="url(https://evil.test/p.svg#g)"` names a
 * resource, and it passes the URL allowlist, which reads `url(` as a plain word. So the hook below puts
 * every presentation attribute through the same value check as an inline declaration.
 */
const REPORT_SVG_TAGS = ['svg', 'g', 'path', 'line', 'polyline', 'circle', 'rect', 'text', 'title', 'desc'];

const REPORT_SVG_ATTR = ['viewBox', 'preserveAspectRatio', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd',
  'points', 'fill', 'stroke', 'stroke-width', 'stroke-linejoin', 'stroke-linecap', 'stroke-dasharray', 'opacity',
  'text-anchor', 'dominant-baseline', 'font-size', 'font-weight'];

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * Tags that must never survive, in either policy.
 *
 * `script` is here even though React's `createElement` almost certainly renders it inert — "almost
 * certainly" is not a security argument, and the cost of the line is nothing. `button` is not in this
 * shared list because the report policy allows it for its actions; prose adds it back. `svg` is, and
 * the report policy takes it back out for a chart's narrow subset (`REPORT_SVG_TAGS`).
 */
const FORBID_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'input',
  'textarea', 'select', 'option', 'link', 'meta', 'base', 'noscript', 'template', 'svg', 'math'];

const FORBID_ATTR = ['srcset', 'formaction', 'xlink:href', 'ping'];

/**
 * The CSS a report may carry inline: a property allowlist, and a check on every value.
 *
 * ⚠ DOMPURIFY DOES NOT SANITISE CSS INSIDE A `style` ATTRIBUTE. Measured:
 * `<div style="background:url(javascript:alert(1))">` and `url(https://evil.test/x)` both come
 * back untouched when `style` is allowed. The `javascript:` form is inert in every current browser,
 * but `url(https://…)` is a live exfiltration channel — a crafted vendor name becomes a request to
 * someone else's server carrying whatever the URL encodes, from an authenticated session.
 *
 * ⚠ THE LIST IS THE TEMPLATES' DESIGN VOCABULARY, AND WHAT IT LEAVES OUT IS LOST SILENTLY. The first
 * version held twenty properties chosen by judgement. Measured against the reference template
 * (lab-demos), rendered in the app beside its standalone render, it dropped fifteen the design uses
 * — `background` among them, so every bar was invisible in the app while the standalone render
 * looked perfect, and every payload check still passed. On the list, and why:
 *
 *  - every property the reference template's report primitives declare;
 *  - the positional longhands of a shorthand already here — `margin-top` is no riskier than `margin`;
 *  - what the plan requires by name: `min-height`, for 44px action targets (WIT-215).
 *
 * Anything else stays off until a template needs it — render a new template in the app and compare it
 * with its standalone render before assuming the list covers it. Never on the list, whatever a
 * template wants:
 *
 *  - a property that takes an image or a URL — `background-image`, `border-image`, `list-style`,
 *    `cursor`, `content`, `mask`, `filter`, `clip-path`. `background` is admitted only because the
 *    value check refuses every function that could carry a URL;
 *  - anything that lets the report paint outside its box — `position`, `inset`, `z-index`,
 *    `transform`. A fixed overlay saying "Session expired — sign in again" runs no script and makes
 *    no request; it is a phishing page drawn inside an authenticated one.
 */
const SAFE_STYLE_PROPS = new Set([
  // box
  'width', 'height', 'min-width', 'max-width', 'min-height',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left', 'border-color', 'border-radius',
  'border-collapse',
  'box-shadow', 'overflow',
  // layout
  'display', 'flex', 'flex-direction', 'flex-wrap', 'gap', 'align-items', 'align-self', 'justify-content',
  'grid-template-columns',
  // text
  'color', 'font-size', 'font-weight', 'font-style', 'font-variant-numeric', 'letter-spacing',
  'text-align', 'text-transform', 'text-overflow', 'white-space',
  // paint
  'background', 'background-color',
]);

/**
 * The functions a value may call. Any other one drops the declaration: `url()`; `image-set()` and its
 * `-webkit-` twin, which fetch a bare string without ever writing `url(`; `cross-fade()`; the old IE
 * `expression()`. An allowlist, because a list of the functions that reach the network is only ever
 * the list somebody remembered.
 */
const SAFE_FUNCTIONS = new Set([
  'var', 'calc', 'min', 'max', 'clamp', 'rgb', 'rgba', 'hsl', 'hsla', 'repeat', 'minmax', 'fit-content',
]);

const isSafeValue = (value) => {
  // A backslash is a CSS escape, which spells `url` where no text check can see it (`u\72 l(`); `@`
  // only ever opens an at-rule; `!` and a comment are how an `!important` hides from the strip below.
  if (/[\\@!]|\/\*/.test(value)) return false;
  // Every `(` must open a known function — which also refuses a bare nested paren inside `calc()`.
  return [...value.matchAll(/([a-z-]*)\(/gi)].every(([, name]) => SAFE_FUNCTIONS.has(name.toLowerCase()));
};

/**
 * A `style` value reduced to the declarations that pass, each judged on its own — a bar that loses one
 * property still reads, where dropping the whole attribute would lose the bar.
 *
 * ⚠ `!important` IS STRIPPED, NOT OBEYED. Inline styles already beat every stylesheet rule, and the
 * one thing that beats them back is an `!important` in the stylesheet — which is how app-desktop's
 * print rules and interaction states have to win (WIT-214, WIT-215). An inline `!important` would take
 * that last word away from the app, so the declaration stays and the flag goes.
 */
const sanitizeStyleValue = (value) => String(value)
  .split(';')
  .map((declaration) => {
    const at = declaration.indexOf(':');
    if (at < 0) return null;
    const prop = declaration.slice(0, at).trim().toLowerCase();
    const body = declaration.slice(at + 1).replace(/!\s*important\s*$/i, '').trim();
    return body && SAFE_STYLE_PROPS.has(prop) && isSafeValue(body) ? `${prop}:${body}` : null;
  })
  .filter(Boolean)
  .join('; ');

// ⚠ REGISTERED ONCE, AT MODULE LOAD. `addHook` is global to DOMPurify, so registering it per call
// would stack a new copy on every message rendered — a leak that shows up as the chat getting
// slower the longer it is open.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!node.getAttribute) return;
  // A report action is a button and only ever a button: it can never submit, and it carries an action
  // only if app-desktop handles that action. Prose never gets here — its policy removes buttons.
  if (node.nodeName === 'BUTTON') node.setAttribute('type', 'button');
  if (node.hasAttribute('data-action') && !REPORT_ACTIONS.has(node.getAttribute('data-action'))) {
    node.removeAttribute('data-action');
  }
  // An angle belongs to a pivot button only, and is a lowercase identifier (WIT-246); anything else is removed.
  if (node.hasAttribute('data-angle') && !(node.getAttribute('data-action') === REPORT_ACTION.PIVOT
    && REPORT_ANGLE_PATTERN.test(node.getAttribute('data-angle')))) {
    node.removeAttribute('data-angle');
  }
  // ⚠ AN ACTION AND A LINK MAY NEVER BE THE SAME PRESS. Both `<a href>` and a `data-action` button are
  // allowed, and nesting one in the other survives the parser, so `<a href="https://evil.test">` around
  // a "Download PDF" button borrows that button's trust: one press prints and navigates this tab away.
  // The action is what gives way, not the link — a report's link still reads as a link.
  if (node.nodeName === 'BUTTON' && node.closest('a')) node.removeAttribute('data-action');
  if (node.nodeName === 'A' && node.closest('button[data-action]')) node.removeAttribute('href');
  // An SVG presentation attribute is CSS by another name: a value that calls anything but the safe
  // functions — `url()` above all — is removed, exactly as a `style` declaration would be.
  if (node.namespaceURI === SVG_NAMESPACE) {
    REPORT_SVG_ATTR.forEach((name) => {
      if (node.hasAttribute(name) && !isSafeValue(node.getAttribute(name))) node.removeAttribute(name);
    });
  }
  if (!node.hasAttribute('style')) return;
  const safe = sanitizeStyleValue(node.getAttribute('style'));
  if (safe) node.setAttribute('style', safe);
  else node.removeAttribute('style');
});

const purify = (html, config) => {
  if (typeof html !== 'string' || !html) return '';
  // ⚠ `RETURN_TRUSTED_TYPE: false` and a string return, because the output is handed straight to
  // `html-react-parser`, which wants a string. Returning a TrustedHTML object here would stringify
  // to `[object TrustedHTML]` and render the literal text — a silent, total loss of the message.
  return String(DOMPurify.sanitize(html, {
    ALLOWED_URI_REGEXP: SAFE_URL,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    RETURN_TRUSTED_TYPE: false,
    ...config,
  }));
};

/**
 * Prose from a model or a user: chat messages, agent summaries, the policy preview.
 *
 * Deliberately narrower than the report policy — nothing here needs `style`, so nothing here gets
 * it. `img` is absent for the same reason: no agent path renders one today, and an image is an
 * outbound request carrying a URL a model chose. `button` is forbidden outright rather than merely
 * left off the list: a chat message has no actions for app-desktop to wire.
 */
export const sanitizeProseHtml = (html) => purify(html, {
  ALLOWED_TAGS: PROSE_TAGS,
  ALLOWED_ATTR: PROSE_ATTR,
  FORBID_TAGS: [...FORBID_TAGS, 'button'],
});

/**
 * A server-rendered report (`content_type: "html"`).
 *
 * Wider than prose because the report's own design requires it — see `REPORT_TAGS`, and
 * `REPORT_SVG_TAGS` for a chart. Still refuses every scripting surface, and still applies the URL
 * allowlist, so a crafted vendor name that escaped the template's autoescape cannot become a link that
 * runs code.
 */
export const sanitizeReportHtml = (html) => purify(html, {
  ALLOWED_TAGS: [...REPORT_TAGS, ...REPORT_SVG_TAGS],
  ALLOWED_ATTR: [...REPORT_ATTR, ...REPORT_SVG_ATTR],
  // Prose keeps refusing `<svg>`; a report may draw a chart with the subset above.
  FORBID_TAGS: FORBID_TAGS.filter((tag) => tag !== 'svg'),
});
